/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved. Unauthorized copying, distribution, or use is prohibited.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Breadcrumbs, Button, Chip, Link, Tooltip } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import { Progress } from '@backstage/core-components';
import { RepoGraph } from './RepoGraph';
import { buildLevelDoc, fleetRepoHandoff, fleetStaleHandoff } from './dirOverviewModel';
import { KgDirtree, KgDoc, KgSummary } from './types';

// Overview-first drill-in for repos too large to lay out whole (PLAT-1534):
// each level is the incumbent renderer drawing a synthetic doc whose nodes
// are the child directories and whose edges are the build-time aggregates;
// the moment a directory fits the renderable class it opens as a real
// symbol shard with the full RepoGraph experience.
export const DirOverview = (props: {
  slug: string;
  summary: KgSummary;
  // Controlled: the page owns the drill path (URL + sidebar tree both steer it).
  path: string;
  onNavigate: (path: string) => void;
  loadDirtree: (slug: string) => Promise<KgDirtree>;
  loadDirShard: (slug: string, key: string) => Promise<KgDoc>;
  meta?: import('./useKnowledgeGraphApi').KgRepoMeta | null;
  onClose: () => void;
  // Global only: the fleet tree stops at repo rows, which open the repo's own graph (PLAT-1646);
  // tail is the path inside that repo when an older fleet deep link pointed below the repo row.
  onOpenRepo?: (slug: string, tail?: string) => void;
}) => {
  const [tree, setTree] = useState<KgDirtree | null>(null);
  const path = props.path;
  const [shardDoc, setShardDoc] = useState<KgDoc | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const shardCache = useRef(new Map<string, KgDoc>());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    props
      .loadDirtree(props.slug)
      .then(t => {
        if (!cancelled) setTree(t);
      })
      .catch(e => {
        if (!cancelled) setError(String((e as Error).message ?? e));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.slug]);

  const byPath = useMemo(() => {
    const m = new Map<string, KgDirtree['dirs'][number]>();
    for (const d of tree?.dirs ?? []) m.set(d.path, d);
    return m;
  }, [tree]);

  // Resolve the current path: a sharded dir renders its real symbols, any
  // other dir renders its children as a level.
  useEffect(() => {
    let cancelled = false;
    const dir = path ? byPath.get(path) : undefined;
    if (!dir?.shard) {
      setShardDoc(null);
      // An interrupted in-flight shard load skips its own finally (cancelled
      // gate) — a level view must not inherit its spinner.
      setLoading(false);
      return undefined;
    }
    const key = dir.shard;
    const cached = shardCache.current.get(key);
    if (cached) {
      setShardDoc(cached);
      // Same rule as the level branch: an interrupted in-flight load's
      // finally never runs, so this arrival must clear the spinner too.
      setLoading(false);
      return undefined;
    }
    // Clear before fetching: a direct shard->shard jump must not flash the
    // previous shard's symbols under the new breadcrumb.
    setShardDoc(null);
    setLoading(true);
    props
      .loadDirShard(props.slug, key)
      .then(doc => {
        if (cancelled) return;
        shardCache.current.set(key, doc);
        setShardDoc(doc);
      })
      .catch(e => !cancelled && setError(String((e as Error).message ?? e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byPath, path, props.slug]);

  // A deep link or breadcrumb that lands on a fleet repo row (or below one, from an older
  // build) goes straight to that repo.
  useEffect(() => {
    if (!props.onOpenRepo || !tree) return;
    const slug = fleetRepoHandoff(tree, path);
    if (slug) {
      props.onOpenRepo(slug);
      return;
    }
    const stale = fleetStaleHandoff(tree, path);
    if (stale) props.onOpenRepo(stale.slug, stale.tail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, path]);

  const openDir = useCallback(
    (target: string) => {
      const dir = byPath.get(target);
      if (!dir) return;
      setError(null);
      const repo = props.onOpenRepo && tree ? fleetRepoHandoff(tree, target) : null;
      if (repo) {
        props.onOpenRepo!(repo);
        return;
      }
      if (!dir.shard && !tree?.dirs.some(x => x.parent === target)) {
        // A directory too dense to shard and too flat to subdivide (vendored
        // header piles). Name the reality instead of opening an empty level.
        setError(
          `"${target}" holds ${dir.symbols.toLocaleString()} symbols in one directory — too dense to open as a graph yet`,
        );
        return;
      }
      props.onNavigate(target);
    },
    [byPath, tree, props],
  );

  const crumbTo = (target: string) => {
    setError(null);
    props.onNavigate(target);
  };

  const deadEnd = useMemo(() => {
    if (!tree || !path) return null;
    if (props.onOpenRepo && fleetRepoHandoff(tree, path)) return null;
    const dir = byPath.get(path);
    if (!dir || dir.shard) return null;
    return tree.dirs.some(d => d.parent === path) ? null : dir;
  }, [tree, path, byPath, props.onOpenRepo]);

  // A path this build doesn't have (an old deep link, a renamed dir) says so instead of drawing an empty level.
  const notFound = useMemo(() => {
    if (!tree || !path || byPath.has(path)) return false;
    return !(props.onOpenRepo && fleetStaleHandoff(tree, path));
  }, [tree, path, byPath, props.onOpenRepo]);

  const levelDoc = useMemo(
    () => (!tree || shardDoc || deadEnd || notFound ? null : buildLevelDoc(tree, path)),
    [tree, path, shardDoc, deadEnd, notFound],
  );

  // Under an org row the children are repos, and opening one leaves this view for the repo's graph.
  const opensRepos = !!props.onOpenRepo && !!path && byPath.get(path)?.parent === '';

  const crumbs = path ? path.split('/') : [];
  const repoName = props.slug.split('/')[1] ?? props.slug;
  const sha = props.summary.built_at_commit;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <Tooltip
          title={sha ? `built from commit ${sha}` : 'artifact carries no build commit'}
        >
          <Chip
            size="small"
            color="primary"
            label={`${props.slug}${sha ? ` @ ${sha.slice(0, 7)}` : ''} · ${props.summary.nodes.toLocaleString()} symbols`}
            onDelete={props.onClose}
            deleteIcon={<CancelIcon aria-label={`close ${props.slug} overview, back to Global`} />}
          />
        </Tooltip>
        <Breadcrumbs aria-label={`directory path inside ${props.slug}`}>
          <Link component="button" variant="body2" onClick={() => crumbTo('')}>
            {repoName}
          </Link>
          {crumbs.map((seg, i) => {
            const target = crumbs.slice(0, i + 1).join('/');
            const last = i === crumbs.length - 1;
            return last ? (
              <span key={target} aria-current="page" style={{ fontWeight: 600 }}>
                {seg}
              </span>
            ) : (
              <Link key={target} component="button" variant="body2" onClick={() => crumbTo(target)}>
                {seg}
              </Link>
            );
          })}
        </Breadcrumbs>
      </div>
      {error && (
        <div role="alert" style={{ marginBottom: 8 }}>
          couldn&apos;t open — {error}{' '}
          <Button size="small" variant="outlined" onClick={() => setError(null)}>
            Dismiss
          </Button>
        </div>
      )}
      {loading && <Progress />}
      {deadEnd && (
        <div role="status" style={{ padding: 16, fontSize: 13 }}>
          &quot;{deadEnd.path}&quot; holds {deadEnd.symbols.toLocaleString()} symbols in one
          directory — too dense to open as a graph yet.{' '}
          <Button
            size="small"
            variant="outlined"
            onClick={() => crumbTo(deadEnd.parent)}
          >
            Back to {deadEnd.parent ? deadEnd.parent.split('/').pop() : repoName}
          </Button>
        </div>
      )}
      {notFound && (
        <div role="status" style={{ padding: 16, fontSize: 13 }}>
          &quot;{path}&quot; isn&apos;t in this build of the graph.{' '}
          <Button size="small" variant="outlined" onClick={() => crumbTo('')}>
            Back to {repoName}
          </Button>
        </div>
      )}
      {shardDoc ? (
        <RepoGraph
          key={`${props.slug}//${path}`}
          doc={shardDoc}
          repoName={`${props.slug} · ${path}`}
          meta={props.meta}
        />
      ) : levelDoc ? (
        <RepoGraph
          key={`${props.slug}//dirs//${path || '(top)'}`}
          doc={levelDoc}
          repoName={`${props.slug} · directories of ${path || '/'}`}
          meta={props.meta}
          onOpenNode={openDir}
          openNodeLabel={opensRepos ? 'Open repo graph' : 'Open directory'}
          dirMode
        />
      ) : null}
    </>
  );
};
